<template>
  <main class="h-full w-full text-sm pb-10 px-4">
    <!-- Search bar -->
    <section class="my-4 mx-2">
      <div class="flex items-center justify-between">
        <h1 class="text-lg font-semibold text-gray-700 mb-2 dark:text-warm-gray-50">
          Find a user
        </h1>
        <router-link to="/v2/users" class="text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md px-2.5 py-1 dark:text-warm-gray-200 dark:hover:text-white dark:border-warm-gray-500">
          <span>Legacy View</span>
        </router-link>
      </div>

      <div class="flex gap-2">
        <select
          v-model="searchField"
          class="text-sm rounded-md border-gray-300 px-3 py-2 focus:border-yellow-500 focus:ring-yellow-500 shadow-sm whitespace-nowrap dark:bg-warm-gray-800 dark:border-warm-gray-600 dark:text-warm-gray-100"
        >
          <option value="email">Email</option>
          <option value="phone">Contact Number</option>
          <option value="firstname">First Name</option>
          <option value="lastname">Last Name</option>
        </select>
        <div class="relative rounded-md shadow-sm flex-1">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg
            class="h-5 w-5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>
        </div>
        <input
          v-model="searchQuery"
          @keyup.enter="searchUser"
          type="text"
          name="search-key"
          id="search-key"
          class="text-sm block w-full rounded-md border-gray-300 pl-9 pr-24 py-3 focus:border-yellow-500 focus:ring-yellow-500 dark:bg-warm-gray-800 dark:border-warm-gray-600 dark:text-warm-gray-100 dark:placeholder-warm-gray-500"
          :placeholder="getPlaceholderText()"
          aria-describedby="search-key"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-3">
          <button
            type="button"
            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:bg-warm-gray-700 dark:border-warm-gray-500 dark:text-warm-gray-100 dark:hover:bg-warm-gray-600"
            :disabled="!searchQuery || isSearching"
            @click="searchUser"
          >
            <span v-if="!isSearching">Search</span>
            <span v-else>Searching...</span>
          </button>
        </div>
        </div>
      </div>

      <p class="mt-2 text-xs text-gray-500 dark:text-warm-gray-400">
        <span v-if="searchField === 'email' || searchField === 'phone'">
          Searching by exact {{ searchField === 'email' ? 'email' : 'contact number' }} match.
        </span>
        <span v-else>
          Searching by {{ searchField === 'firstname' ? 'first name' : 'last name' }} prefix (starts with).
        </span>
      </p>
    </section>

    <!-- Results -->
    <section class="mx-2 mt-4">
      <div v-if="errorMessage" class="text-xs text-red-600 mb-2 dark:text-red-400">
        {{ errorMessage }}
      </div>

      <div v-if="!isSearching && searched && users.length === 0" class="text-sm text-gray-500 italic dark:text-warm-gray-400">
        No users found for "<span class="font-semibold">{{ searchQuery }}</span>".
      </div>

      <ul v-if="users.length > 0" class="space-y-3">
        <li
          v-for="user in users"
          :key="user.id"
          class="border border-gray-200 rounded-md p-4 bg-white dark:bg-warm-gray-800 dark:border-warm-gray-600"
        >
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div class="space-y-1 min-w-0 flex-1">
              <p class="text-sm font-semibold text-gray-800 dark:text-warm-gray-50">
                {{ user.data.firstname }} {{ user.data.lastname }}
              </p>
              <p class="text-xs text-gray-600 dark:text-warm-gray-300">
                {{ user.data.email || '---' }}
                <button
                  v-if="user.data.email"
                  type="button"
                  class="ml-1 inline-flex align-middle text-warm-gray-400 hover:text-yellow-600"
                  title="Copy email"
                  @click.stop="copyText(user.data.email)"
                >
                  <ClipboardDocumentIcon class="w-4 h-4" />
                </button>
              </p>
              <p class="text-xs text-gray-600 dark:text-warm-gray-300">
                Contact: {{ user.data.phone || '---' }}
                <button
                  v-if="user.data.phone"
                  type="button"
                  class="ml-1 inline-flex align-middle text-warm-gray-400 hover:text-yellow-600"
                  title="Copy contact number"
                  @click.stop="copyText(user.data.phone)"
                >
                  <ClipboardDocumentIcon class="w-4 h-4" />
                </button>
              </p>
              <p class="text-xs text-gray-600 dark:text-warm-gray-300">
                School: {{ schoolName(user.data.school) }}
              </p>
              <p class="text-xs text-gray-400 mt-1 dark:text-warm-gray-500">
                ID: {{ user.id }}
              </p>
              <p class="text-xs text-gray-600 dark:text-warm-gray-300">
                Skillpoints: {{ user.data.skillpoints != null && user.data.skillpoints !== '' ? user.data.skillpoints : '---' }}
              </p>
              <p class="text-xs text-gray-600 dark:text-warm-gray-300">
                Date Signed Up: {{ formatUserDate(user.data.date_created) }}
              </p>
            </div>
            <div class="flex flex-col gap-2 sm:flex-shrink-0">
              <router-link
                :to="`/v2/user/${user.id}`"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:bg-warm-gray-700 dark:border-warm-gray-500 dark:text-warm-gray-100 dark:hover:bg-warm-gray-600"
              >
                View full profile
              </router-link>
              <button
                @click="navigateToPurchase(user.id)"
                class="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 shadow-sm hover:bg-blue-100 dark:bg-blue-900 dark:bg-opacity-30 dark:border-blue-700 dark:text-blue-200 dark:hover:bg-blue-900 dark:hover:bg-opacity-50"
              >
                Purchase Subscription
              </button>
            </div>
          </div>

          <!-- Reviewers & status -->
          <div v-if="user.data.reviewers" class="mt-3 pt-3 border-t border-gray-100 dark:border-warm-gray-600">
            <p class="text-xs font-semibold text-gray-700 mb-1.5 dark:text-warm-gray-200">
              Reviewers
            </p>
            <div class="flex flex-col gap-2 md:flex-row md:items-stretch md:gap-3">
              <template v-for="(status, reviewerKey) in user.data.reviewers" :key="reviewerKey">
                <div
                  v-if="reviewerKey !== 'caap_lite'"
                  class="flex-1 min-w-0 rounded-md border border-gray-200 bg-gray-50 p-2.5 dark:border-warm-gray-600 dark:bg-warm-gray-700"
                >
                  <div
                    :class="['inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium', getReviewerBadgeClass(status)]"
                  >
                    <span class="uppercase mr-1">
                      {{ mapReviewerKeyToLabel(reviewerKey) }}
                    </span>
                    <span>
                      ({{ status || 'unknown' }})
                    </span>
                    <button
                      v-if="(status || '').toLowerCase() === 'ready' || (status || '').toLowerCase() === 'active'"
                      type="button"
                      :class="[
                        (isRevoking(user.id, reviewerKey)) ? 'animate-spin' : '',
                        'ml-1 text-[10px] text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed dark:text-red-400 dark:hover:text-red-300']"
                      :disabled="isRevoking(user.id, reviewerKey)"
                      @click.stop="revokeReviewer(user, reviewerKey)"
                    >
                      (X)
                    </button>
                  </div>
                  <div class="mt-2 space-y-1 text-xs">
                    <div class="flex items-baseline justify-between gap-2">
                      <span class="flex-shrink-0 text-gray-500 dark:text-warm-gray-400">Purchase Date</span>
                      <span class="text-right text-gray-800 dark:text-warm-gray-100">
                        {{ formatReviewerDate(getReviewerData(user.data, reviewerKey), 'purchase') }}
                      </span>
                    </div>
                    <div class="flex items-baseline justify-between gap-2">
                      <span class="flex-shrink-0 text-gray-500 dark:text-warm-gray-400">Activation Date</span>
                      <span class="text-right text-gray-800 dark:text-warm-gray-100">
                        {{ formatReviewerDate(getReviewerData(user.data, reviewerKey), 'activation') }}
                      </span>
                    </div>
                    <div class="flex items-baseline justify-between gap-2">
                      <span class="flex-shrink-0 text-gray-500 dark:text-warm-gray-400">Expiration Date</span>
                      <span class="text-right text-gray-800 dark:text-warm-gray-100">
                        {{ formatReviewerDate(getReviewerData(user.data, reviewerKey), 'expiration') }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </li>
      </ul>
    </section>
  </main>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { db, functions } from "@/firebase/config";
import { collection, query, where, getDocs, limit, doc, updateDoc, onSnapshot, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ClipboardDocumentIcon } from "@heroicons/vue/24/outline";

const router = useRouter();

const searchQuery = ref("");
const searchField = ref("email");
const isSearching = ref(false);
const searched = ref(false);
const errorMessage = ref("");
const users = ref([]);
const revokingMap = ref({});
const userListeners = ref(new Map()); // Track active listeners: userId -> unsubscribe function
const schoolsById = ref({});

const schoolName = (schoolId) => {
  if (!schoolId) return "---";
  const school = schoolsById.value[schoolId];
  return school && school.name ? school.name : "---";
};

const formatUserDate = (value) => {
  if (value == null || value === "") return "---";
  let ms = null;
  if (typeof value === "object") {
    if (typeof value.toMillis === "function") ms = value.toMillis();
    else if (value.seconds != null) ms = value.seconds * 1000;
    else if (value._seconds != null) ms = value._seconds * 1000;
  } else {
    const n = parseInt(value, 10);
    if (!Number.isNaN(n)) {
      ms = String(Math.abs(n)).length <= 10 ? n * 1000 : n;
    }
  }
  if (!ms) return "---";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "---";
  const months = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.", "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const getReviewerData = (userData, reviewerKey) => {
  if (!userData || !reviewerKey) return null;
  return userData[`${reviewerKey}_data`] || null;
};

const formatReviewerDate = (reviewerData, field) => {
  if (!reviewerData) return "---";
  if (field === "purchase") {
    return formatUserDate(reviewerData.purchase_date_v2 || reviewerData.purchase_date);
  }
  if (field === "activation") {
    return formatUserDate(reviewerData.activation_date);
  }
  if (field === "expiration") {
    return formatUserDate(reviewerData.expiration_date);
  }
  return "---";
};

const copyText = async (value) => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
  } catch (err) {
    console.log("Copy failed", err);
  }
};

const loadSchools = async () => {
  try {
    const snapshot = await getDocs(collection(db, "schools"));
    const map = {};
    snapshot.forEach((schoolDoc) => {
      map[schoolDoc.id] = schoolDoc.data();
    });
    schoolsById.value = map;
  } catch (err) {
    console.error("Error loading schools:", err);
  }
};

const getReviewerBadgeClass = (status) => {
  const normalized = (status || "").toString().toLowerCase();

  if (normalized === "active") {
    return "bg-green-100 text-green-800 border border-green-200 dark:bg-green-900 dark:bg-opacity-40 dark:text-green-200 dark:border-green-700";
  }

  if (normalized === "pending" || normalized === "ready") {
    return "bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900 dark:bg-opacity-40 dark:text-blue-200 dark:border-blue-700";
  }

  if (normalized === "inactive" || normalized === "") {
    return "bg-gray-100 text-gray-700 border border-gray-200 dark:bg-warm-gray-700 dark:text-warm-gray-200 dark:border-warm-gray-500";
  }

  return "bg-gray-100 text-gray-700 border border-gray-200 dark:bg-warm-gray-700 dark:text-warm-gray-200 dark:border-warm-gray-500";
};

const mapReviewerKeyToLabel = (key) => {
  switch (key) {
    case "caap":
      return "CAAP Knowledge";
    case "caap_plus":
      return "CAAP Practical";
    case "mtp":
      return "MTP Entrance";
    default:
      return key.toUpperCase();
  }
};

const getPlaceholderText = () => {
  switch (searchField.value) {
    case "email":
      return "Search user by email";
    case "phone":
      return "Search user by contact number";
    case "firstname":
      return "Search user by first name";
    case "lastname":
      return "Search user by last name";
    default:
      return "Search user";
  }
};

const isRevoking = (userId, reviewerKey) => {
  const k = `${userId}::${reviewerKey}`;
  return !!revokingMap.value[k];
};

const revokeReviewer = async (user, reviewerKey) => {
  if (!user || !user.id || !user.data || !user.data.email) return;

  const confirmed = window.confirm(`Revoke ${mapReviewerKeyToLabel(reviewerKey)} for this user?`);
  if (!confirmed) return;

  try {
    const mapKey = `${user.id}::${reviewerKey}`;
    revokingMap.value[mapKey] = true;

    // Update Firebase custom claims
    let funcPayload = {};
    let keysToremove = [];

    switch (reviewerKey) {
      case "caap":
        funcPayload = { caap: false };
        keysToremove.push("caapExpAt");
        break;
      case "caap_plus":
        funcPayload = { caap_plus: false };
        keysToremove.push("plusExpAt");
        break;
      case "mtp":
        funcPayload = { mtp: false };
        keysToremove.push("mtpExpAt");
        break;
      default:
        console.error(`Unknown reviewer key: ${reviewerKey}`);
        return;
    }

    const updateUserClaimsFbFunc = httpsCallable(functions, "updateUserClaims");
    const updateUserClaimsResponse = await updateUserClaimsFbFunc({
      email: user.data.email,
      payload: funcPayload,
      toRemoveKeys: keysToremove,
    });

    if (!updateUserClaimsResponse.data.success) {
      console.error(`Failed to update claims for ${reviewerKey.toUpperCase()}`);
      return;
    }

    // Update user document in Firestore
    let _data;
    let _reviewers;

    switch (reviewerKey) {
      case "caap":
        _data = {
          caap_data: {
            purchase_date: "",
            gcash_refno: "",
          },
        };
        _reviewers = {
          "reviewers.caap": "inactive",
        };
        break;
      case "caap_plus":
        _data = {
          caap_plus_data: {
            purchase_date: "",
            gcash_refno: "",
          },
        };
        _reviewers = {
          "reviewers.caap_plus": "inactive",
        };
        break;
      case "mtp":
        _data = {
          mtp_data: {
            purchase_date: "",
            gcash_refno: "",
          },
        };
        _reviewers = {
          "reviewers.mtp": "inactive",
        };
        break;
      default:
        return;
    }

    const userDocRef = doc(db, "users", user.id);
    await updateDoc(userDocRef, { ..._reviewers, ..._data });

    // Update local copy so UI stays in sync
    if (user.data.reviewers) {
      user.data.reviewers[reviewerKey] = "inactive";
    }
  } catch (error) {
    console.error(`Error revoking ${reviewerKey.toUpperCase()} access:`, error);
  } finally {
    const mapKey = `${user.id}::${reviewerKey}`;
    revokingMap.value[mapKey] = false;
  }
};

/**
 * Set up a real-time listener for a user document
 * @param {string} userId - The user document ID
 */
const setupUserListener = (userId) => {
  // If listener already exists, don't set up another one
  if (userListeners.value.has(userId)) {
    return;
  }

  const userDocRef = doc(db, "users", userId);
  const unsubscribe = onSnapshot(
    userDocRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const updatedData = docSnapshot.data();
        
        // Find and update the user in the users array
        const userIndex = users.value.findIndex((u) => u.id === userId);
        if (userIndex !== -1) {
          // Update the user data while preserving the object reference structure
          users.value[userIndex] = {
            id: docSnapshot.id,
            data: updatedData,
          };
        }
      }
    },
    (error) => {
      console.error(`Error listening to user ${userId}:`, error);
    }
  );

  // Store the unsubscribe function
  userListeners.value.set(userId, unsubscribe);
};

/**
 * Clean up all active user listeners
 */
const cleanupAllListeners = () => {
  userListeners.value.forEach((unsubscribe, userId) => {
    unsubscribe();
  });
  userListeners.value.clear();
};

/**
 * Clean up listener for a specific user
 * @param {string} userId - The user document ID
 */
const cleanupUserListener = (userId) => {
  const unsubscribe = userListeners.value.get(userId);
  if (unsubscribe) {
    unsubscribe();
    userListeners.value.delete(userId);
  }
};

const searchUser = async () => {
  const trimmed = searchQuery.value.trim();
  if (!trimmed) {
    // Clean up all listeners when clearing search
    cleanupAllListeners();
    users.value = [];
    errorMessage.value = "";
    searched.value = false;
    return;
  }

  // Clean up existing listeners before new search
  cleanupAllListeners();

  isSearching.value = true;
  errorMessage.value = "";
  searched.value = true;
  users.value = [];

  try {
    const usersRef = collection(db, "users");
    let q;
    const field = searchField.value;

    if (field === "email" || field === "phone") {
      // Exact match for email and phone
      q = query(usersRef, where(field, "==", trimmed), limit(10));
    } else if (field === "firstname" || field === "lastname") {
      // Case-insensitive prefix search for firstname and lastname
      // Query multiple case variations and filter client-side
      const searchTermLower = trimmed.toLowerCase();
      const searchTermUpper = trimmed.toUpperCase();
      const searchTermCapitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
      
      // Create queries for different case variations
      const queries = [
        query(
          usersRef,
          where(field, ">=", searchTermLower),
          where(field, "<=", searchTermLower + "\uf8ff"),
          orderBy(field),
          limit(50)
        ),
        query(
          usersRef,
          where(field, ">=", searchTermUpper),
          where(field, "<=", searchTermUpper + "\uf8ff"),
          orderBy(field),
          limit(50)
        ),
        query(
          usersRef,
          where(field, ">=", searchTermCapitalized),
          where(field, "<=", searchTermCapitalized + "\uf8ff"),
          orderBy(field),
          limit(50)
        )
      ];

      // Execute all queries in parallel
      const snapshots = await Promise.all(queries.map(q => getDocs(q)));
      
      // Combine results and deduplicate
      const userMap = new Map();
      snapshots.forEach(snapshot => {
        snapshot.forEach((doc) => {
          if (!userMap.has(doc.id)) {
            userMap.set(doc.id, {
              id: doc.id,
              data: doc.data(),
            });
          }
        });
      });

      // Filter client-side for case-insensitive prefix match
      const filteredResults = Array.from(userMap.values()).filter((user) => {
        const fieldValue = (user.data[field] || "").toString();
        return fieldValue.toLowerCase().startsWith(searchTermLower);
      });

      // Limit to 10 results
      users.value = filteredResults.slice(0, 10);

      // Set up listeners for all found users
      users.value.forEach((user) => {
        setupUserListener(user.id);
      });

      // Skip the rest of the function since we've already processed results
      return;
    } else {
      // Fallback to email search
      q = query(usersRef, where("email", "==", trimmed), limit(10));
    }

    const snapshot = await getDocs(q);

    const results = [];
    snapshot.forEach((doc) => {
      results.push({
        id: doc.id,
        data: doc.data(),
      });
    });

    users.value = results;

    // Set up listeners for all found users
    results.forEach((user) => {
      setupUserListener(user.id);
    });
  } catch (error) {
    console.error("Error searching users:", error);
    errorMessage.value = "Something went wrong while searching. Please try again.";
  } finally {
    isSearching.value = false;
  }
};

const navigateToPurchase = (userId) => {
  router.push(`/v2/purchase?userId=${userId}`);
};

onMounted(() => {
  loadSchools();
});

// Clean up all listeners when component unmounts
onUnmounted(() => {
  cleanupAllListeners();
});
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>


